import {inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, shareReplay} from 'rxjs/operators';
import {WorkflowService} from '../../../services/workflow.service';
import {StateConfigService} from '../../../services/state/state-config.service';
import {DocumentClassService} from '../../files/components/document-classes/document-class.service';
import {SelectOption} from './schema-form.types';

/**
 * Resolves dynamic option sources referenced by a schema property's `x-source`.
 *
 * Adding a new source is a one-time, generic addition here (never per-module).
 * Results are cached per source (+ project for project-scoped sources) so multiple
 * fields sharing a source fetch once.
 */
@Injectable({providedIn: 'root'})
export class SchemaSourceService {
  private readonly flow = inject(WorkflowService);
  private readonly state = inject(StateConfigService);
  private readonly documentClasses = inject(DocumentClassService);
  private readonly cache = new Map<string, Observable<SelectOption[]>>();

  getOptions(source: string): Observable<SelectOption[]> {
    const projectId = this.state.getProjectId();
    const key = `${source}:${projectId ?? ''}`;
    let stream = this.cache.get(key);
    if (!stream) {
      stream = this.build(source, projectId).pipe(
        catchError(() => of([] as SelectOption[])),
        shareReplay(1),
      );
      this.cache.set(key, stream);
    }
    return stream;
  }

  private build(source: string, projectId: number | undefined | null): Observable<SelectOption[]> {
    switch (source) {
      case 'foundry-models':
        return this.flow.listAiModels().pipe(
          map(models => (models ?? []).map(m => ({value: m.name, label: m.name}))),
        );
      case 'document-classes':
        if (!projectId) return of([]);
        return this.documentClasses.list(projectId).pipe(
          map(res => (res.classes ?? [])
            .map(c => ({value: c.id ?? '', label: c.title ?? ''}))
            .filter(o => o.value)),
        );
      default:
        return of([]);
    }
  }
}
